import React, { useState } from 'react';
import { useAdVocalizeCore } from '../context/AdVocalizeCore';
import { Upload, Zap } from 'lucide-react';

const SAMPLES = [
  { id: 'christmas',  label: 'Festival Sale',  category: 'Retail · Promo',    url: '/sample_images/festival_sale.jpg' },
  { id: 'food',       label: 'Food & Beverage',category: 'Restaurant · Café',  url: '/sample_images/food_sample.jpeg' },
  { id: 'hiring',     label: 'Hiring Campaign',category: 'Recruitment · HR',   url: '/sample_images/hiring_sample.webp' },
  { id: 'realestate', label: 'Real Estate',    category: 'Property · Luxury',  url: '/sample_images/real_estate.png' },
];

const Step0_Upload = () => {
    const { setImage, setStep, runMasterPipeline, setAuthError } = useAdVocalizeCore();
    const [hoveredSample, setHoveredSample] = useState(null);

    const handleImageUpload = (file) => {
        if (!file) return;
        const r = new FileReader();
        r.onload = (ev) => {
            const b64 = ev.target.result;
            setImage(b64);
            setStep(1);
            runMasterPipeline(b64);
        };
        r.readAsDataURL(file);
    };

    const loadSampleImage = (url) => {
        setAuthError('');
        fetch(url)
            .then(r => r.blob())
            .then(blob => {
                const reader = new FileReader();
                reader.onload = (e) => {
                    setImage(e.target.result);
                    setStep(1);
                    runMasterPipeline(e.target.result);
                };
                reader.readAsDataURL(blob);
            })
            .catch(() => setAuthError('Could not load sample image.'));
    };

    return (
        <div className="max-w-5xl mx-auto px-8 pt-3 pb-8">
            <div className="bg-white/5 border border-white/10 rounded-[2.5rem] overflow-hidden backdrop-blur-3xl shadow-2xl">

                {/* Card header — title sits flush below the strip */}
                <div className="px-10 py-7 flex items-center justify-between border-b border-white/[0.06]">
                    <div>
                        <h2 className="text-2xl font-black tracking-tight text-white">
                            Upload your{' '}
                            <span className="bg-gradient-to-r from-indigo-400 via-purple-400 to-orange-400 bg-clip-text text-transparent">
                                visual
                            </span>
                        </h2>
                        <p className="text-slate-500 text-sm font-medium mt-1">
                            AI reads your image and scripts a voice ad in seconds
                        </p>
                    </div>
                    <div className="hidden lg:flex items-center gap-2 px-3 py-1.5 bg-indigo-500/10 border border-indigo-500/20 rounded-full">
                        <Zap className="w-3.5 h-3.5 text-indigo-400" />
                        <span className="text-[10px] font-bold text-indigo-300/80">Each take is a real Director run</span>
                    </div>
                </div>

                {/* Drop zone */}
                <div className="px-10 py-8">
                    <label
                        htmlFor="v3Upload"
                        className="group relative flex items-center gap-8 border-2 border-dashed border-white/10 rounded-2xl p-10 bg-white/[0.02] hover:bg-indigo-500/[0.04] hover:border-indigo-500/40 transition-all duration-500 cursor-pointer"
                        onDragOver={e => e.preventDefault()}
                        onDrop={e => { e.preventDefault(); handleImageUpload(e.dataTransfer.files[0]); }}
                    >
                        <div className="w-16 h-16 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center text-indigo-400 group-hover:scale-110 group-hover:bg-indigo-600/20 group-hover:border-indigo-500/50 transition-all duration-500 shadow-xl shrink-0">
                            <Upload className="w-8 h-8" />
                        </div>
                        <div>
                            <h3 className="text-xl font-black text-white">Click to upload or drag &amp; drop</h3>
                            <p className="text-slate-500 font-mono text-xs tracking-wider uppercase mt-1">PNG · JPG · WEBP — Any Size</p>
                        </div>
                    </label>
                    <input
                        id="v3Upload"
                        type="file"
                        className="hidden"
                        accept="image/*"
                        onChange={e => handleImageUpload(e.target.files[0])}
                    />
                </div>

                {/* Divider */}
                <div className="flex items-center gap-6 px-10 pb-4">
                    <div className="h-px bg-white/5 flex-1" />
                    <span className="text-[10px] font-black text-slate-600 uppercase tracking-[0.5em]">Or try a sample take</span>
                    <div className="h-px bg-white/5 flex-1" />
                </div>

                {/* Samples grid */}
                <div className="px-10 pb-10 grid grid-cols-2 sm:grid-cols-4 gap-5">
                    {SAMPLES.map(sample => (
                        <button
                            key={sample.id}
                            onClick={() => loadSampleImage(sample.url)}
                            onMouseEnter={() => setHoveredSample(sample.id)}
                            onMouseLeave={() => setHoveredSample(null)}
                            className="group relative bg-white/[0.02] border border-white/5 rounded-2xl p-3 transition-all duration-500 hover:-translate-y-1 hover:bg-white/5 hover:border-indigo-500/30 hover:shadow-[0_16px_32px_rgba(99,102,241,0.15)]"
                        >
                            <div className="relative aspect-[4/5] rounded-xl overflow-hidden bg-black/40 mb-3">
                                <img
                                    src={sample.url}
                                    alt={sample.label}
                                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-60" />
                                <div className={`absolute top-3 right-3 px-2.5 py-1 rounded-full bg-indigo-600 text-white text-[9px] font-black uppercase tracking-widest shadow-lg transition-all duration-300 ${hoveredSample === sample.id ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-3'}`}>
                                    Try →
                                </div>
                            </div>
                            <div className="text-center">
                                <h4 className="font-black text-white text-sm tracking-tight">{sample.label}</h4>
                                <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest mt-0.5">{sample.category}</p>
                            </div>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default Step0_Upload;
